// The dossier as the owner reads it before a call (04 §2, Продажби tab).
//
// Order is deliberate and matches how the call goes: what this is, who to ask for, what is
// uncertain, what is wrong with the site, what to ask, what not to say.
//
// Data-quality flags sit ABOVE the findings, not buried at the bottom. If a phone is
// unconfirmed the owner needs to know that before dialling, not after.

import type { DossierContent, StoredDocument } from './useDossier.js';
import { StateIcon } from '../../components/StateIcon.js';

const CATEGORY_LABELS: Record<string, string> = {
  legal: 'Правни и съответствие',
  capabilities: 'Възможности и резервации',
  freshness: 'Актуалност',
  performance: 'Скорост и техническо',
  visibility: 'Видимост и доверие',
};

export type DossierMeta = Pick<
  StoredDocument<unknown>,
  'model' | 'generated_at' | 'llm_error' | 'status'
>;

/**
 * Who wrote this and when. For weeks every document was a template because the model answered
 * 404, then 402, and nothing on screen said so — the row carried `model = NULL` and the owner
 * read the template as the product. The provenance line is the fix: template or model, and why.
 */
function provenance(meta: DossierMeta): string {
  const when = meta.generated_at
    ? new Date(meta.generated_at).toLocaleDateString('bg-BG', { dateStyle: 'medium' })
    : '';
  if (meta.model) return `Текст: ${meta.model}${when ? ` · ${when}` : ''}`;
  const why =
    meta.status === 'needs_review'
      ? 'AI текстът беше задържан за проверка'
      : meta.llm_error
        ? `моделът отказа: ${meta.llm_error.slice(0, 120)}`
        : 'без модел';
  return `Машинен шаблон — ${why}${when ? ` · ${when}` : ''}`;
}

function OwnerSection({ owner }: { owner: DossierContent['owner_block'] }) {
  if (!owner.name && !owner.direct_phone) return null;
  const formattedPhone =
    owner.direct_phone && !owner.direct_phone.startsWith('+')
      ? `+359 ${owner.direct_phone}`
      : owner.direct_phone;
  return (
    <section className="dossier__owner">
      <h3 className="dossier__h">👤 Ключово лице</h3>
      <p className="dossier__owner-name">
        <strong>{owner.name ?? 'Неизвестен'}</strong>{' '}
        {owner.role ? <span className="badge badge--neutral">{owner.role}</span> : ''}
      </p>
      {owner.direct_phone ? (
        <p className="dossier__owner-phone">
          <a className="btn btn--secondary btn--sm" href={telHref(owner.direct_phone)}>
            📞 {formattedPhone}
          </a>
        </p>
      ) : null}
    </section>
  );
}

function normalizeOpeningHours(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string');
  if (raw && typeof raw === 'object') {
    const wd = (raw as { weekdayDescriptions?: unknown }).weekdayDescriptions;
    if (Array.isArray(wd)) return wd.filter((v): v is string => typeof v === 'string');
  }
  return [];
}

function ReputationSection({ rep }: { rep: DossierContent['reputation'] }) {
  if (!rep) return null;
  const hours = normalizeOpeningHours(rep.opening_hours);
  const reviews = rep.top_reviews ?? [];
  if (rep.rating === null && hours.length === 0 && reviews.length === 0) return null;

  return (
    <section className="dossier__reputation">
      <div className="dossier__sec-head">
        <h3 className="dossier__h">⭐ Репутация и отзиви</h3>
        {rep.rating !== null ? (
          <span className="badge badge--warn">
            {rep.rating} ★ ({rep.review_count ?? 0} отзива в Google)
          </span>
        ) : null}
      </div>
      {hours.length > 0 ? <p className="dossier__muted">{hours.join(' · ')}</p> : null}
      {reviews.length > 0 ? (
        <ul className="dossier__reviews">
          {reviews.map((rev, idx) => {
            const relTime = rev.relative_time ?? rev.relativeTime;
            return (
              <li key={idx}>
                {rev.rating !== null ? <strong>{rev.rating}★ </strong> : null}„{rev.text}“
                {rev.author ? ` — ${rev.author}` : ''}
                {relTime ? ` (${relTime})` : ''}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function LegalSection({ legal }: { legal: DossierContent['legal_block'] }) {
  if (!legal || (!legal.eik && !legal.legal_name)) return null;
  const financials = legal.financials ?? [];
  return (
    <section className="dossier__legal">
      <div className="dossier__sec-head">
        <h3 className="dossier__h">🏛️ Юридическо лице и финанси</h3>
        {legal.eik ? <span className="badge badge--info">ЕИК {legal.eik}</span> : null}
      </div>
      <p className="dossier__legal-line">
        <strong>{legal.legal_name ?? '—'}</strong>
        {legal.company_status ? ` · ${legal.company_status}` : ''}
        {legal.registration_date ? ` · рег. ${legal.registration_date}` : ''}
      </p>
      {financials.length > 0 ? (
        <div className="tbl__wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Година</th>
                <th className="tbl__num">Приходи (лв.)</th>
                <th className="tbl__num">Печалба (лв.)</th>
                <th className="tbl__num">Персонал</th>
              </tr>
            </thead>
            <tbody>
              {financials.map((f) => (
                <tr key={f.year}>
                  <td>{f.year}</td>
                  <td className="tbl__num">
                    {f.revenue_bgn !== null ? f.revenue_bgn.toLocaleString('bg-BG') : '—'}
                  </td>
                  <td className="tbl__num">
                    {f.profit_bgn !== null ? f.profit_bgn.toLocaleString('bg-BG') : '—'}
                  </td>
                  <td className="tbl__num">{f.employees ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export function Dossier({ content, meta }: { content: DossierContent; meta?: DossierMeta | null }) {
  return (
    <div className="dossier">
      <div className="dossier__hero">
        <div className="dossier__hero-top">
          <span className="badge badge--info">Резюме за разговор</span>
          {meta ? <span className="dossier__provenance">{provenance(meta)}</span> : null}
        </div>
        <p className="dossier__summary">{content.summary}</p>
      </div>

      {content.data_quality_flags.length > 0 ? (
        <section className="dossier__flags" aria-label="Качество на данните">
          {content.data_quality_flags.map((flag) => (
            <p key={flag} className="dossier__flag">
              ⚠️ {flag}
            </p>
          ))}
        </section>
      ) : null}

      <div className="dossier__bento">
        <OwnerSection owner={content.owner_block} />
        <ReputationSection rep={content.reputation} />
      </div>

      <LegalSection legal={content.legal_block} />

      {content.branches.length > 0 ? (
        <section className="dossier__branches">
          <h3 className="dossier__h">🏢 Обекти на същия сайт</h3>
          <ul>
            {content.branches.map((branch) => (
              <li key={`${branch.businessName}-${branch.address ?? ''}`}>
                <strong>{branch.businessName}</strong>
                {branch.address ? ` — ${branch.address}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {content.categories.map((section) => (
        <section key={section.category} className="cat">
          <h3 className="cat__title">{CATEGORY_LABELS[section.category] ?? section.category}</h3>
          <div className="cat__findings">
            {section.findings.map((finding) => (
              <article key={finding.checkKey} className="finding">
                <div className="finding__icon">
                  <StateIcon state={finding.state} />
                </div>
                <div className="finding__body">
                  <p className="finding__title">{finding.titleBg}</p>
                  {finding.detailBg ? <p className="finding__detail">{finding.detailBg}</p> : null}
                  {finding.consequenceBg ? (
                    <p className="finding__detail">
                      <strong>Цена за бизнеса:</strong> {finding.consequenceBg}
                    </p>
                  ) : null}
                  {finding.evidence ? (
                    <p className="dossier__muted">Доказателство: {finding.evidence}</p>
                  ) : null}
                  {finding.improvementBg ? (
                    <p className="finding__fix">→ {finding.improvementBg}</p>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}

      <section className="dossier__prep">
        <div className="dossier__prep-col">
          <div className="dossier__sec-head">
            <h3 className="dossier__h">🎯 Въпроси за разговора</h3>
            {content.call_prep.dead_hours ? (
              <span className="badge badge--warn">Не звънете: {content.call_prep.dead_hours}</span>
            ) : null}
          </div>
          <ul className="dossier__questions">
            {content.call_prep.discovery_questions.map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </div>

        {content.call_prep.what_not_to_say.length > 0 ? (
          <div className="dossier__avoid-box">
            <h4 className="dossier__h4">⛔ Не казвайте</h4>
            <ul className="dossier__avoid">
              {content.call_prep.what_not_to_say.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {content.score_breakdown.length > 0 ? (
        <details className="dossier__score">
          <summary>Как е сметната оценката ({content.score_breakdown.length} фактора)</summary>
          <ul>
            {content.score_breakdown.map((entry) => (
              <li key={entry.reason}>
                <span>{entry.reason}</span>
                <span className="badge badge--score">
                  {entry.points > 0 ? '+' : ''}
                  {entry.points}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/** A stored number may already be E.164 (+359…); prefixing it again dialled +359+359…. */
function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return `tel:${digits}`;
  if (digits.startsWith('00')) return `tel:+${digits.slice(2)}`;
  return `tel:+359${digits.replace(/^0/, '')}`;
}
